import useConditionalEffect from 'hooks/useConditionalEffect'
import {
  AnyContent,
  Article,
  ARTICLE_TYPE,
  Feed,
  Gif,
  GIF_TYPE,
  Photo,
  PHOTO_TYPE,
  Text,
  Video,
  VIDEO_TYPE
} from 'interfaces'
import { TEXT_TYPE } from 'interfaces/Content/ContentType'
import {
  STOCK_ALL_FILTER,
  STOCK_GIF_TYPE,
  STOCK_PHOTO_TYPE,
  STOCK_VIDEO_TYPE
} from 'interfaces/Content/StockContentTypes'
import { FC, memo, useCallback, useRef } from 'react'
import { animationFrameScheduler, Observable, of as observableOf, Subscription } from 'rxjs'
import { mergeAll, observeOn, toArray } from 'rxjs/operators'
import { createHandledPipe } from './FetchError'
import { FilterType } from 'shared'

export interface FetchFeedContentProps {
  feed: Feed
  hash?: string
  only?: FilterType

  getVideos(): Observable<Video>
  getPhotos(): Observable<Photo>
  getArticles(): Observable<Article>
  getGifs(): Observable<Gif>
  getTexts(): Observable<Text>
  getStockVideos(): Observable<Video>
  getStockPhotos(): Observable<Photo>
  getStockGifs(): Observable<Gif>
  getStockAll(): Observable<AnyContent>
  onFetched(content: AnyContent[]): void
  onFailed?(err: any): void
}

export const FetchFeedContent: FC<FetchFeedContentProps> = memo(({
  feed,
  only,
  hash,
  getVideos,
  getPhotos,
  getArticles,
  getGifs,
  getTexts,
  getStockVideos,
  getStockPhotos,
  getStockGifs,
  getStockAll,
  onFetched,
  onFailed
}) => {
  const subscriptionRef = useRef<Subscription | null>(null)

  const handleFailed = useCallback((err: any) => {
    console.log(err)
    if (onFailed) {
      onFailed(err)
    }
  }, [onFailed])

  const fetch = useCallback(() => {
    let contentTypes = feed.sources.reduce((result, type) => {
      result[type] = !only || only === type ? true : false
      return result
    }, {} as { [key: string]: boolean })

    // If we don't have the types of sources for that feed, fetch photos, videos and articles by default
    if (feed.sources.length === 0) {
      contentTypes[VIDEO_TYPE] = true
      contentTypes[PHOTO_TYPE] = true
      contentTypes[ARTICLE_TYPE] = true
    }

    if (only === STOCK_ALL_FILTER) {
      contentTypes = {
        [STOCK_ALL_FILTER]: true
      }
    }

    const requests: Observable<any>[] = []
    if (contentTypes[VIDEO_TYPE]) {
      requests.push(createHandledPipe<AnyContent>(VIDEO_TYPE, getVideos(), handleFailed))
    }

    if (contentTypes[PHOTO_TYPE]) {
      requests.push(createHandledPipe<AnyContent>(PHOTO_TYPE, getPhotos(), handleFailed))
    }

    if (contentTypes[ARTICLE_TYPE]) {
      requests.push(createHandledPipe<AnyContent>(ARTICLE_TYPE, getArticles(), handleFailed))
    }

    if (contentTypes[GIF_TYPE]) {
      requests.push(createHandledPipe<AnyContent>(GIF_TYPE, getGifs(), handleFailed))
    }

    if (contentTypes[TEXT_TYPE]) {
      requests.push(createHandledPipe<AnyContent>(TEXT_TYPE, getTexts(), handleFailed))
    }

    if (contentTypes[STOCK_VIDEO_TYPE]) {
      requests.push(createHandledPipe<AnyContent>(STOCK_VIDEO_TYPE, getStockVideos(), handleFailed))
    }

    if (contentTypes[STOCK_PHOTO_TYPE]) {
      requests.push(createHandledPipe<AnyContent>(STOCK_PHOTO_TYPE, getStockPhotos(), handleFailed))
    }

    if (contentTypes[STOCK_GIF_TYPE]) {
      requests.push(createHandledPipe<AnyContent>(STOCK_GIF_TYPE, getStockGifs(), handleFailed))
    }

    if (contentTypes[STOCK_ALL_FILTER]) {
      requests.push(createHandledPipe<AnyContent>(STOCK_ALL_FILTER, getStockAll(), handleFailed))
    }

    return observableOf(...requests)
      .pipe(
        mergeAll(),
        toArray(),
        observeOn(animationFrameScheduler)
      )
      .subscribe({ next: onFetched, error: handleFailed })
  }, [
    feed.sources,
    only,
    getVideos,
    getPhotos,
    getArticles,
    getGifs,
    getTexts,
    getStockVideos,
    getStockPhotos,
    getStockGifs,
    getStockAll,
    onFetched,
    handleFailed
  ])

  useConditionalEffect(([fetchContent]) => {
    subscriptionRef.current = fetchContent()

    return () => {
      subscriptionRef.current?.unsubscribe()
    }
  }, [hash], [fetch])

  return null
}, (prevProps, nextProps) => {
  return prevProps.hash === nextProps.hash
    && prevProps.feed.id === nextProps.feed.id
    && prevProps.only === nextProps.only
})

export default FetchFeedContent
